// `Scorecard` (13 §Shapes): what `GET /seats/{id}/scorecard`, `GET /scorecards/company` and a
// `scorecard_publication.snapshot` carry. Field semantics are domain-kpi's (`scoreOwnMetrics`,
// `finalBand`, `propagate`); `scorecard.test.ts` pins the enums to the engine's types.
import { z } from "zod";
import {
  Band,
  Currency,
  MetricDirection,
  MetricUnit,
  ObservationSource,
  PeriodType,
  ScoreState,
  SubjectKind,
  TeamRollup,
  TrackingMode,
  Visibility,
} from "./enums";
import { IsoDate, PeriodKey, Timestamp, Uuid } from "./primitives";
import { PendingReview, ReportingStatus } from "./reporting";

export const ScorecardSubject = z
  .object({ kind: SubjectKind, id: Uuid, name: z.string() })
  .meta({ id: "ScorecardSubject" });

export const ScorecardPeriod = z
  .object({ type: PeriodType, key: PeriodKey, starts_at: Timestamp, ends_at: Timestamp })
  .meta({ id: "ScorecardPeriod" });

export const TrendPoint = z.object({
  period_key: PeriodKey,
  actual: z.number().nullable(),
  target: z.number().nullable(),
});

/**
 * AGENT-KPI-TOOL: where an agent-reported number came from (`outcomes.record`): the run that
 * reported it, the DE, and the evidence the agent gave (text, and a link when it has one). The
 * run and the agent come from the run's credential, never from the request body.
 */
export const ObservationProvenance = z
  .object({
    run_id: Uuid,
    agent_membership_id: Uuid,
    agent_name: z.string().max(200).nullable(),
    evidence_text: z.string().min(1).max(1000),
    evidence_url: z.string().max(2000).nullable(),
    /**
     * AGENT-REPORTING: a value of a `needs_review` metric, recorded when a person decided its
     * `kpi_value` approval: approved as reported, or edited (the agent's value kept here).
     */
    review: z
      .object({
        approval_id: Uuid,
        state: z.enum(["approved", "edited"]),
        decided_by_membership_id: Uuid,
        original_value: z.number().nullable(),
      })
      .optional(),
  })
  .meta({ id: "ObservationProvenance" });
export type ObservationProvenance = z.infer<typeof ObservationProvenance>;

/**
 * AGENT-KPI-TOOL: the metric's newest live value on the seat (in the scorecard's trend window):
 * its period, value and source, and the agent's provenance when a DE's run reported it (an
 * `agent` value, or the agent's event behind an event-sourced value).
 */
export const LastReport = z
  .object({
    period_key: PeriodKey,
    value: z.number(),
    source: ObservationSource,
    recorded_at: Timestamp,
    provenance: ObservationProvenance.nullable(),
  })
  .meta({ id: "LastReport" });
export type LastReport = z.infer<typeof LastReport>;

/**
 * TEAM-KPI (founder-approved 2026-10-21): a metric's own warning bands on target attainment, in
 * percent — green ≥ `green`, amber ≥ `amber`, red below (domain-kpi `metricHealth`). Absent or
 * null: the company's fixed bands (`DEFAULT_METRIC_BANDS`, 100 / 70).
 */
export const MetricBands = z
  .strictObject({ green: z.number().min(0).max(150), amber: z.number().min(0).max(150) })
  .refine((b) => b.amber <= b.green, {
    path: ["amber"],
    message: "amber starts at or below green",
  })
  .meta({ id: "MetricBands" });
export type MetricBands = z.infer<typeof MetricBands>;

/**
 * PLATFORM-OPERATOR: a metric's gate spec (`metric_def.gate`; domain-kpi `GateSpec`). A metric of
 * the platform operator's own company that carries one is a gate on the operator's gate tracker:
 * what it measures, its condition text, count start, fallback target and due date, kill lines.
 * The company's gates are KPI data, never code.
 */
const GateText = z.string().min(1).max(500);
export const MetricGate = z
  .strictObject({
    order: z.int().min(0).max(10_000),
    measure: z.enum(["sum", "mean", "latest", "ratio", "fanout", "baseline"]),
    condition: GateText,
    count_from: IsoDate.nullable().optional(),
    due_on: IsoDate.nullable().optional(),
    target: z.number().nullable().optional(),
    target_max: z.number().nullable().optional(),
    notes_as_reasons: z.boolean().optional(),
    ratio: z
      .strictObject({ numerator: z.string().max(64), denominator: z.string().max(64) })
      .optional(),
    fanout: z.enum(["supervision", "founder"]).optional(),
    baseline: z
      .strictObject({
        period_key: PeriodKey,
        starts_on: IsoDate,
        label: GateText,
        review_at: z.number().min(0).max(10),
        halt_at: z.number().min(0).max(10),
        review_text: GateText.nullable().optional(),
        halt_text: GateText.nullable().optional(),
      })
      .optional(),
    notes: z
      .strictObject({
        none: GateText.nullable().optional(),
        below: GateText.nullable().optional(),
        above: GateText.nullable().optional(),
      })
      .optional(),
    kill: z
      .array(z.strictObject({ text: GateText, due_on: IsoDate, below: z.number() }))
      .max(20)
      .optional(),
  })
  .meta({ id: "MetricGate" });
export type MetricGate = z.infer<typeof MetricGate>;

/** A metric line's health under its bands (null: no attainment — grey, never red). */
export const MetricHealth = z.enum(["green", "amber", "red"]).meta({ id: "MetricHealth" });
export type MetricHealth = z.infer<typeof MetricHealth>;

export const ScorecardMetric = z
  .object({
    metric_def_id: Uuid,
    key: z.string(),
    name: z.string(),
    unit: MetricUnit,
    direction: MetricDirection,
    visibility: Visibility,
    actual: z.number().nullable(),
    target: z.number().nullable(),
    target_max: z.number().nullable(),
    attainment: z.number().nullable(),
    expected: z.number(),
    present: z.number(),
    completeness: z.number(),
    tracking_mode: TrackingMode,
    state: ScoreState,
    kpi_weight: z.number(),
    goal_weight: z.number(),
    /** The last 13 periods of the same type, oldest first (KPIS-MODE; was 8). */
    trend: z.array(TrendPoint).max(13),
    /** AGENT-KPI-TOOL: the newest live value and who reported it (absent in older snapshots). */
    last_report: LastReport.nullable().optional(),
    /** AGENT-REPORTING: the seat's reporting duty for the metric, now (absent: no duty). */
    reporting: ReportingStatus.nullable().optional(),
    /** AGENT-REPORTING: an agent value waiting for review (excluded from every number here). */
    pending_review: PendingReview.nullable().optional(),
    /**
     * TEAM-KPI: the metric's own bands (null: the company's), and the line's health under them.
     * Optional: a snapshot frozen before 2026-10-21 has neither (read it with the company's).
     */
    bands: MetricBands.nullable().optional(),
    health: MetricHealth.nullable().optional(),
    /** TEAM-KPI: on a team scorecard, where the value comes from (`none`: the team's own). */
    rollup: TeamRollup.optional(),
  })
  .meta({ id: "ScorecardMetric" });

/** domain-kpi `Contributor`: what moved the score. */
export const Contributor = z
  .object({
    kind: z.enum(["metric", "child"]),
    id: z.string(),
    name: z.string(),
    weight: z.number(),
    shortfall: z.number(),
    impact: z.number(),
  })
  .meta({ id: "Contributor" });

export const ScorecardChild = z
  .object({
    subject: ScorecardSubject,
    kpi_score: z.number().nullable(),
    goal_score: z.number().nullable(),
    completeness: z.number(),
    band: Band,
    weight: z.number(),
    /**
     * The child's own metric rows for the period (HOME-CAD-UX 2026-10-21), so the company
     * scorecard grid draws every seat from this one read. A client view carries the client
     * rows only.
     */
    metrics: z.array(ScorecardMetric).optional(),
  })
  .meta({ id: "ScorecardChild" });

export const Scorecard = z
  .object({
    subject: ScorecardSubject,
    period: ScorecardPeriod,
    computed_at: Timestamp,
    kpi_score: z.number().nullable(),
    goal_score: z.number().nullable(),
    completeness: z.number(),
    band: Band,
    metrics: z.array(ScorecardMetric),
    contributors: z.array(Contributor),
    /**
     * Company roll-up only: every seat with KPIs, the DE seats and the person-held seats that
     * carry a scored metric (so a human-only company still has a score).
     */
    children: z.array(ScorecardChild).optional(),
    cost: z
      .object({
        total_minor: z.int(),
        currency: Currency,
        by_kind: z.record(z.string(), z.int()),
      })
      .nullable(),
    /** `founder_minutes` is null unless the viewer is an agency operator. */
    hours: z
      .object({ supervision_minutes: z.int(), founder_minutes: z.int().nullable() })
      .nullable(),
  })
  .meta({ id: "Scorecard" });
export type Scorecard = z.infer<typeof Scorecard>;

/**
 * The scorecard a client may see (13 §Friday report: client-visibility rows only; agency
 * metrics and founder hours never appear). `FridayReport` embeds it, so an agency row in an
 * emailed snapshot is a validation error, not a review finding (finding #2).
 */
export const ClientScorecardMetric = z
  .strictObject({ ...ScorecardMetric.shape, visibility: z.literal("client") })
  .meta({ id: "ClientScorecardMetric" });

export const ClientScorecard = z
  .strictObject({
    ...Scorecard.shape,
    metrics: z.array(ClientScorecardMetric),
    contributors: z.array(Contributor),
    hours: z
      .strictObject({ supervision_minutes: z.int(), founder_minutes: z.null() })
      .nullable()
      .describe("founder_minutes is always null in a client view"),
  })
  .meta({ id: "ClientScorecard" });
export type ClientScorecard = z.infer<typeof ClientScorecard>;
